// src/hooks/useGameWinCondition.js

import { useMemo, useCallback, useEffect } from 'react';
import { db } from '../firebase'; 
import { doc, updateDoc } from 'firebase/firestore'; 

import { 
    WINNING_PERCENTAGE, 
    MAX_TURNS
} from '../constants/gameConstants'; 

export const useGameWinCondition = (roomCode, gameState, players, myPlayerIndex) => {
    
    const totalCells = useMemo(() => {
        if (!gameState) return 0;
        return gameState.gridW * gameState.gridH;
    }, [gameState]);

    const territoryCounts = useMemo(() => {
        if (!gameState || !players.length || totalCells === 0) return [];

        const counts = new Array(players.length).fill(0);
        for (const ownerIndex of gameState.grid) {
            if (ownerIndex >= 0 && ownerIndex < players.length) {
                counts[ownerIndex]++;
            }
        }
        return counts;
    }, [gameState, players, totalCells]);

    const potentialWinnerIndex = useMemo(() => {
        if (!gameState || gameState.winner !== null || gameState.status === 'finished') return null; 

        const minCellsForWin = Math.ceil(totalCells * WINNING_PERCENTAGE); 

        // % şartını kontrol et
        for (let i = 0; i < players.length; i++) {
            if (territoryCounts[i] >= minCellsForWin) {
                return i;
            }
        }

        // MAX_TURNS'e ulaştık mı? (turn >= MAX_TURNS olduğunda)
        if (gameState.turn >= MAX_TURNS && gameState.turn > 0) { 
            let maxCount = -1;
            let winnerIndex = -1;
            
            territoryCounts.forEach((count, index) => {
                if (count > maxCount) {
                    maxCount = count;
                    winnerIndex = index;
                } 
            });

            return winnerIndex >= 0 ? winnerIndex : null;
        }

        return null;
    }, [gameState, players.length, territoryCounts, totalCells]);


    const finalizeGame = useCallback(async (winnerIndex) => {
        if (!roomCode || winnerIndex === null || (gameState && gameState.winner !== null)) return;

        const winnerPlayer = players[winnerIndex];

        if (!winnerPlayer) {
            console.error('Kazanan oyuncu bulunamadı.');
            return;
        }

        const roomRef = doc(db, 'rooms', roomCode);
        
        const winConditionText = gameState.turn >= MAX_TURNS 
            ? `Tur limiti (${MAX_TURNS}) sonunda en çok kareye sahip olarak` 
            : `karelerin %${WINNING_PERCENTAGE * 100}'sinden fazlasına hakim olarak`;

        try {
            await updateDoc(roomRef, {
                'gameState.winner': winnerPlayer.id, 
                'gameState.log': [
                    ...(gameState.log || []),
                    `🏆 ${winnerPlayer.name} oyunu kazandı! (${territoryCounts[winnerIndex]} kare ile, ${winConditionText}!)`
                ],
                'status': 'finished', 
                lastUpdated: new Date()
            });
        } catch (error) {
            console.error('Oyun sonuçlandırma hatası:', error);
        }
    }, [roomCode, players, gameState?.log, gameState?.winner, gameState?.turn, territoryCounts]);


    const checkAndFinalizeWin = useCallback(() => {
        if (gameState && gameState.winner !== null) return false; 
        
        const winnerIndex = potentialWinnerIndex;
        
        if (winnerIndex !== null) {
            const isHost = myPlayerIndex !== -1 && players[myPlayerIndex]?.isHost;
            
            if (isHost) {
                finalizeGame(winnerIndex); 
            }
            return true; 
        }
        return false;
    }, [gameState, potentialWinnerIndex, finalizeGame, myPlayerIndex, players]);

    // ⭐ ÇÖZÜM: Bot tamamlandıktan sonra otomatik kontrol et
    useEffect(() => {
        if (!gameState || gameState.winner !== null) return;
        
        const myPlayerIsHost = myPlayerIndex !== -1 && players[myPlayerIndex]?.isHost;
        if (!myPlayerIsHost) return;

        if (gameState.turn >= MAX_TURNS && gameState.turn > 0) {
            const winnerIndex = potentialWinnerIndex;
            if (winnerIndex !== null) {
                finalizeGame(winnerIndex);
            }
        }
    }, [gameState?.turn, gameState?.winner, potentialWinnerIndex, myPlayerIndex, players, finalizeGame]);

    return {
        territoryCounts,
        checkAndFinalizeWin,
        winnerId: gameState?.winner,
    };
};